import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, ChevronUp, ChevronDown, Eye, EyeOff, Save, Image as ImageIcon, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  adminListWelcomeImages, adminUploadWelcomeImage, adminUpdateWelcomeImage,
  adminReorderWelcomeImages, adminDeleteWelcomeImage
} from '../../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const absUrl = (u) => (u?.startsWith('http') ? u : `${BACKEND_URL}${u}`);

const WelcomeImagesTab = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [altText, setAltText] = useState('');
  const [error, setError] = useState('');
  const [savingCaptionId, setSavingCaptionId] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminListWelcomeImages();
      setImages(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file (JPEG, PNG, WebP, GIF).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be smaller than 10MB.');
      return;
    }
    setUploading(true); setError('');
    try {
      const res = await adminUploadWelcomeImage(file, caption, altText);
      setImages(prev => [...prev, res.data]);
      setCaption('');
      setAltText('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const move = async (index, dir) => {
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= images.length) return;
    const reordered = images.slice();
    [reordered[index], reordered[newIdx]] = [reordered[newIdx], reordered[index]];
    setImages(reordered);
    try {
      await adminReorderWelcomeImages(reordered.map(i => i.image_id));
    } catch (e) {
      alert('Failed to save new order');
      load();
    }
  };

  const toggleActive = async (img) => {
    try {
      const res = await adminUpdateWelcomeImage(img.image_id, { active: !img.active });
      setImages(prev => prev.map(i => (i.image_id === img.image_id ? res.data : i)));
    } catch (e) {
      alert('Failed to update visibility');
    }
  };

  const saveMeta = async (img, updates) => {
    setSavingCaptionId(img.image_id);
    try {
      const res = await adminUpdateWelcomeImage(img.image_id, updates);
      setImages(prev => prev.map(i => (i.image_id === img.image_id ? res.data : i)));
    } catch (e) {
      alert('Failed to save changes');
    } finally {
      setSavingCaptionId(null);
    }
  };

  const remove = async (img) => {
    if (!window.confirm('Remove this welcome picture? This cannot be undone.')) return;
    try {
      await adminDeleteWelcomeImage(img.image_id);
      setImages(prev => prev.filter(i => i.image_id !== img.image_id));
    } catch (e) {
      alert('Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="welcome-images-tab">
      <div className="bg-blue-50 border border-blue-100 text-blue-900 rounded-xl p-3 text-sm flex gap-2">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>These pictures appear on the homepage hero. Upload up to 10 images; they rotate automatically. Toggle any picture off to hide it without deleting.</div>
      </div>

      {/* Upload panel */}
      <div className="bg-white rounded-2xl p-6 shadow-sm space-y-3">
        <h3 className="font-semibold text-gray-900">Add a picture</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-600">Caption (shown on the picture)</Label>
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="e.g. Find your one"
              maxLength={140}
              className="mt-1 rounded-lg"
              data-testid="welcome-caption"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Alt text (accessibility &amp; SEO)</Label>
            <Input
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="e.g. Happy Guyanese couple laughing at sunset"
              maxLength={200}
              className="mt-1 rounded-lg"
              data-testid="welcome-alt-text"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
            data-testid="welcome-file-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || images.length >= 10}
            className="bg-[#009E60] hover:bg-[#007A4B] rounded-full"
            data-testid="welcome-upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading…' : images.length >= 10 ? 'Max 10 reached' : 'Upload picture'}
          </Button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <p className="text-xs text-gray-500">JPEG, PNG, WebP or GIF · max 10MB · {images.length}/10 uploaded. Alt text helps screen readers and Google image search.</p>
      </div>

      {/* Existing images */}
      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-500">Loading…</div>
      ) : images.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center">
          <ImageIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h4 className="font-semibold text-gray-900 mb-1">No welcome pictures yet</h4>
          <p className="text-sm text-gray-500">Upload your first picture — it will show up on the homepage instantly.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {images.map((img, index) => (
            <WelcomeImageCard
              key={img.image_id}
              image={img}
              index={index}
              total={images.length}
              onUp={() => move(index, -1)}
              onDown={() => move(index, 1)}
              onToggleActive={() => toggleActive(img)}
              onSave={(updates) => saveMeta(img, updates)}
              onRemove={() => remove(img)}
              saving={savingCaptionId === img.image_id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const WelcomeImageCard = ({ image, index, total, onUp, onDown, onToggleActive, onSave, onRemove, saving }) => {
  const [captionDraft, setCaptionDraft] = useState(image.caption || '');
  const [altDraft, setAltDraft] = useState(image.alt_text || '');
  const dirty = captionDraft !== (image.caption || '') || altDraft !== (image.alt_text || '');
  const isActive = image.active !== false;

  useEffect(() => { setCaptionDraft(image.caption || ''); }, [image.caption]);
  useEffect(() => { setAltDraft(image.alt_text || ''); }, [image.alt_text]);

  return (
    <div className={`bg-white rounded-2xl overflow-hidden shadow-sm ${!isActive ? 'opacity-70' : ''}`} data-testid={`welcome-item-${image.image_id}`}>
      <div className="relative aspect-[16/10] bg-gray-100">
        <img
          src={absUrl(image.url)}
          alt={image.alt_text || image.caption || 'Welcome picture'}
          className={`w-full h-full object-cover ${!isActive ? 'grayscale' : ''}`}
        />
        <div className="absolute top-2 left-2 flex items-center gap-1">
          <span className="bg-black/60 text-white text-[11px] font-medium px-2 py-0.5 rounded-full">#{index + 1}</span>
          {!isActive && (
            <span className="bg-gray-800 text-white text-[11px] font-medium px-2 py-0.5 rounded-full">Hidden</span>
          )}
          {!image.alt_text && (
            <span className="bg-yellow-500 text-white text-[11px] font-medium px-2 py-0.5 rounded-full" title="Missing alt text">No alt text</span>
          )}
        </div>
        <div className="absolute top-2 right-2 flex flex-col gap-1">
          <button
            onClick={onUp}
            disabled={index === 0}
            className="w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center disabled:opacity-40"
            title="Move up"
            data-testid={`welcome-up-${image.image_id}`}
          >
            <ChevronUp className="w-4 h-4 text-gray-800" />
          </button>
          <button
            onClick={onDown}
            disabled={index === total - 1}
            className="w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center disabled:opacity-40"
            title="Move down"
            data-testid={`welcome-down-${image.image_id}`}
          >
            <ChevronDown className="w-4 h-4 text-gray-800" />
          </button>
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <Label className="text-xs text-gray-600">Caption</Label>
          <Input
            value={captionDraft}
            onChange={(e) => setCaptionDraft(e.target.value)}
            maxLength={140}
            className="mt-1 rounded-lg"
            data-testid={`welcome-caption-input-${image.image_id}`}
          />
        </div>
        <div>
          <Label className="text-xs text-gray-600">Alt text <span className="text-gray-400">(accessibility &amp; SEO)</span></Label>
          <Input
            value={altDraft}
            onChange={(e) => setAltDraft(e.target.value)}
            maxLength={200}
            placeholder="Describe what's in the picture"
            className="mt-1 rounded-lg"
            data-testid={`welcome-alt-input-${image.image_id}`}
          />
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            {isActive ? <Eye className="w-4 h-4 text-emerald-600" /> : <EyeOff className="w-4 h-4 text-gray-500" />}
            <span>{isActive ? 'Visible' : 'Hidden'}</span>
            <Switch checked={isActive} onCheckedChange={onToggleActive} data-testid={`welcome-toggle-${image.image_id}`} />
          </label>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSave({ caption: captionDraft, alt_text: altDraft })}
              disabled={!dirty || saving}
              data-testid={`welcome-save-caption-${image.image_id}`}
            >
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600 hover:bg-red-50"
              onClick={onRemove}
              data-testid={`welcome-remove-${image.image_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeImagesTab;
